import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getBackendUrl, resolveAssetUrl } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import SeoHead from '../components/SeoHead';
import {
  ArrowRight, ArrowLeft, Globe, Users, Target, FileCheck,
  Video, FileSignature, ShieldCheck, Briefcase, Laptop,
  Heart, Clock, MapPin, CheckCircle2
} from 'lucide-react';

const STEPS = [
  {
    icon: Globe,
    title: 'Une équipe dans plusieurs pays',
    text: "Nos représentant·e·s recrutent et accompagnent les talents localement, dans plusieurs pays francophones. Proximité, culture, fuseau horaire — on gère pour vous.",
  },
  {
    icon: Target,
    title: 'Des clients européens qui cherchent des talents',
    text: "Notre gérant prospecte et sélectionne les entreprises européennes qui souhaitent recruter des profils internationaux en télétravail. Vous, vous postulez. Nous, on place.",
  },
  {
    icon: FileCheck,
    title: 'Sélection minutieuse',
    text: "Dès qu'une mission est ouverte, nous étudions votre profil en détail pour trouver l'adéquation parfaite entre vos compétences et le besoin du client.",
  },
  {
    icon: Video,
    title: 'Audition + entretien client',
    text: "Vous passez une première audition avec notre équipe, puis un entretien avec le recruteur final pour valider le fit humain et technique.",
  },
  {
    icon: FileSignature,
    title: 'Contrat avec notre agence',
    text: "Un contrat de travail est signé avec Talent Partner. Nous restons votre interlocuteur unique pour le paiement, les congés, et toute question administrative.",
  },
  {
    icon: ShieldCheck,
    title: 'On s\'occupe de tout',
    text: "Pour vous comme pour le client, nous gérons l'onboarding, le suivi qualité et la relation au quotidien. Vous n'êtes jamais seul·e.",
  },
];

const PROFILES = [
  'Assistant·e Virtuel·le',
  'Community Manager',
  'Gestion Ads / Traffic Manager',
  'SEO / Référencement',
  'Développeur / Tech',
  'Graphiste / Designer',
  'Rédacteur / Copywriter',
  'Monteur Vidéo',
  'Funnel Builder',
  'AI Automation Specialist',
  'Support Client',
  'Commercial / Vente',
];

const COUNTRIES_OPEN = [
  { flag: '🇲🇬', name: 'Madagascar' },
  { flag: '🇨🇲', name: 'Cameroun' },
  { flag: '🇫🇷', name: 'France' },
  { flag: '🇸🇳', name: 'Sénégal' },
  { flag: '🇧🇯', name: 'Bénin' },
  { flag: '🇲🇦', name: 'Maroc' },
  { flag: '🇹🇳', name: 'Tunisie' },
  { flag: '🇩🇿', name: 'Algérie' },
  { flag: '🇲🇺', name: 'Maurice' },
];

const BENEFITS = [
  { icon: Laptop, label: '100% télétravail', desc: 'Travaillez depuis chez vous, en toute flexibilité.' },
  { icon: Clock, label: 'Horaires adaptés', desc: 'Temps plein, temps partiel ou projet — à vous de choisir.' },
  { icon: Heart, label: 'Accompagnement humain', desc: 'Un·e représentant·e dans votre pays, disponible pour vous.' },
  { icon: ShieldCheck, label: 'Contrat sécurisé', desc: 'Rémunération garantie, contrat officiel avec notre agence.' },
];

const PostulerLandingPage = () => {
  const [logoUrl, setLogoUrl] = useState(null);
  const [platformName, setPlatformName] = useState('Talent Partner');

  useEffect(() => {
    fetch(getBackendUrl() + '/api/branding')
      .then(res => res.json())
      .then(data => {
        if (data.logo_light_url) setLogoUrl(data.logo_light_url);
        if (data.platform_name) setPlatformName(data.platform_name);
      })
      .catch(() => {});

    // Funnel: landing view
    import('../lib/funnel').then(({ trackFunnel }) => trackFunnel('landing_view', { source: '/postuler' }));
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SeoHead pageTitle="Rejoignez nos talents | Talent Partner" />

      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-white border-b border-tp-border" data-testid="postuler-landing-nav">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-20">
            <Link to="/" className="flex items-center gap-2 text-sm text-tp-text-light hover:text-tp-primary transition-colors" data-testid="back-to-site">
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">Retour au site</span>
            </Link>
            <div className="flex items-center">
              {logoUrl ? (
                <img src={resolveAssetUrl(logoUrl)} alt={platformName} className="h-10 sm:h-12 max-w-[200px] object-contain" />
              ) : (
                <span className="text-lg font-display font-semibold text-tp-primary">{platformName}</span>
              )}
            </div>
            <div className="w-[100px]" />
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-tp-primary via-tp-primary to-tp-primary-light text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-tp-accent/30 rounded-full blur-3xl" />
          <div className="absolute top-1/2 -left-24 w-80 h-80 bg-tp-accent/20 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 md:py-28">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-3 py-1.5 rounded-full text-xs font-medium mb-6" data-testid="hero-badge">
              <Globe className="h-3.5 w-3.5" />
              Ouvert aux talents francophones du monde entier
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold leading-tight mb-5">
              Votre prochain job <span className="text-tp-accent">100% en télétravail</span> vous attend.
            </h1>
            <p className="text-base sm:text-lg text-white/80 mb-8 leading-relaxed">
              Nous plaçons des talents francophones dans des entreprises européennes exigeantes.
              Sélection rigoureuse, accompagnement humain, contrat sécurisé — vous vous concentrez sur votre métier, on gère tout le reste.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/postuler/formulaire">
                <Button
                  size="lg"
                  className="bg-tp-accent hover:bg-tp-accent-light text-white font-semibold text-base px-8 h-12 w-full sm:w-auto"
                  data-testid="hero-cta-apply"
                >
                  Postuler maintenant <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="#comment-ca-marche">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/30 bg-white/5 text-white hover:bg-white/15 h-12 w-full sm:w-auto"
                  data-testid="hero-cta-process"
                >
                  Comment ça marche
                </Button>
              </a>
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-4 text-xs sm:text-sm text-white/70">
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-tp-accent" /> Réponse sous 72h</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-tp-accent" /> Contrat officiel</span>
              <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-tp-accent" /> Paiement garanti</span>
            </div>
          </div>
        </div>
      </section>

      {/* Who we are */}
      <section className="py-16 sm:py-20 bg-tp-bg/40" data-testid="who-we-are">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="text-xs font-semibold uppercase tracking-wider text-tp-accent">Qui sommes-nous ?</span>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-tp-text mt-2 mb-4">
              Une agence qui connecte les talents francophones aux entreprises européennes
            </h2>
            <p className="text-base sm:text-lg text-tp-text-light leading-relaxed">
              Nous sommes une équipe internationale. Des représentants sur le terrain dans plusieurs pays — pour recruter, accompagner les candidats et suivre les missions — et un gérant qui s'occupe de prospecter les entreprises européennes qui cherchent des profils à l'international.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-3 md:gap-4 max-w-4xl mx-auto">
            {BENEFITS.map((b) => (
              <Card key={b.label} className="p-3 sm:p-4 md:p-5 border-tp-border text-center bg-white hover:shadow-md transition-shadow min-w-0" data-testid={`benefit-${b.label}`}>
                <div className="w-10 h-10 rounded-full bg-tp-accent/10 flex items-center justify-center mx-auto mb-3">
                  <b.icon className="h-5 w-5 text-tp-accent" />
                </div>
                <p className="font-semibold text-[13px] sm:text-sm text-tp-text mb-1 break-words leading-tight">{b.label}</p>
                <p className="text-[11px] sm:text-xs text-tp-text-light leading-relaxed break-words">{b.desc}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section id="comment-ca-marche" className="py-16 sm:py-20 bg-white" data-testid="process-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="text-xs font-semibold uppercase tracking-wider text-tp-accent">Comment ça marche</span>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-tp-text mt-2 mb-4">
              De votre candidature à votre premier contrat
            </h2>
            <p className="text-base sm:text-lg text-tp-text-light">
              Un processus transparent, humain, et pensé pour protéger autant le talent que le client.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {STEPS.map((step, i) => (
              <Card key={step.title} className="p-5 md:p-6 border-tp-border bg-tp-bg/30 hover:bg-white hover:shadow-lg transition-all relative overflow-hidden" data-testid={`step-${i + 1}`}>
                <div className="absolute top-3 right-4 text-5xl font-bold text-tp-primary/5">{String(i + 1).padStart(2, '0')}</div>
                <div className="w-11 h-11 rounded-xl bg-tp-primary text-white flex items-center justify-center mb-4 relative">
                  <step.icon className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold text-tp-text mb-2 relative">{step.title}</h3>
                <p className="text-sm text-tp-text-light leading-relaxed relative">{step.text}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Profiles */}
      <section className="py-16 sm:py-20 bg-tp-bg/40" data-testid="profiles-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="text-xs font-semibold uppercase tracking-wider text-tp-accent">Métiers recherchés</span>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-tp-text mt-2 mb-4">
              Vous travaillez dans le digital ou le visuel ?
            </h2>
            <p className="text-base sm:text-lg text-tp-text-light">
              Tous ces métiers peuvent se faire à distance — et nos clients en cherchent constamment.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-2 sm:gap-3 max-w-4xl mx-auto" data-testid="profiles-list">
            {PROFILES.map((p) => (
              <span
                key={p}
                className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 bg-white border border-tp-border rounded-full text-sm font-medium text-tp-text hover:border-tp-accent hover:text-tp-accent transition-colors"
              >
                <Briefcase className="h-3.5 w-3.5" />
                {p}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Countries */}
      <section className="py-16 sm:py-20 bg-white" data-testid="countries-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-10">
            <span className="text-xs font-semibold uppercase tracking-wider text-tp-accent">Pays ouverts</span>
            <h2 className="text-3xl sm:text-4xl font-display font-bold text-tp-text mt-2 mb-4">
              Peu importe où vous êtes
            </h2>
            <p className="text-base sm:text-lg text-tp-text-light">
              Nous recrutons dans plusieurs pays francophones — et nous ouvrons régulièrement de nouveaux territoires.
            </p>
          </div>
          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {COUNTRIES_OPEN.map((c) => (
              <div key={c.name} className="inline-flex items-center gap-2 px-4 py-2.5 bg-tp-bg/60 rounded-lg border border-tp-border" data-testid={`country-${c.name}`}>
                <span className="text-2xl leading-none">{c.flag}</span>
                <span className="text-sm font-medium text-tp-text">{c.name}</span>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-tp-text-light mt-6 max-w-2xl mx-auto">
            <MapPin className="inline h-3.5 w-3.5 mr-1" />
            Pas listé ? Candidatez quand même — on étudie chaque dossier au cas par cas.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-24 bg-gradient-to-br from-tp-primary to-tp-primary-light text-white" data-testid="final-cta-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur px-3 py-1.5 rounded-full text-xs font-medium mb-6">
            <Users className="h-3.5 w-3.5" />
            Plus de 150 talents déjà placés
          </div>
          <h2 className="text-3xl sm:text-5xl font-display font-bold mb-5 leading-tight">
            Prêt·e à rejoindre l'aventure ?
          </h2>
          <p className="text-base sm:text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            5 minutes pour remplir votre profil. Réponse garantie sous 72 heures.
            Aucun frais de candidature, jamais.
          </p>
          <Link to="/postuler/formulaire">
            <Button
              size="lg"
              className="bg-tp-accent hover:bg-tp-accent-light text-white font-semibold text-base px-10 h-14"
              data-testid="final-cta-apply"
            >
              Je postule maintenant <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
          <p className="mt-6 text-xs text-white/60">
            En postulant, vous acceptez nos CGV et notre politique de confidentialité.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 px-4 bg-tp-primary text-white/70 text-center text-xs border-t border-white/10">
        © 2026 {platformName}. Tous droits réservés. ·
        <Link to="/cgv" className="hover:text-white ml-1">CGV</Link> ·
        <Link to="/mentions-legales" className="hover:text-white ml-1">Mentions légales</Link>
      </footer>
    </div>
  );
};

export default PostulerLandingPage;
